import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { IReferenceSelectionData } from 'projects/content-service-cms/src/app/collection-screen/components/reference-search/reference-selection.component';
import { RRIPagination } from 'projects/web-ui-component-library/src';
import { FilterOutput } from 'projects/web-ui-component-library/src/lib/filter-elements/interfaces/filter.interface';

export interface FilterConfiguration {
  name: string;
  selected: string | string[];
}

export interface SelectionModalState {
  pagination: RRIPagination;
  searchPattern: string;
  filters?: { [key: string]: string | [] };
  visibleFilter: string;
  garmentsLoaded: boolean;
  garmentsLoading: boolean;
  allGarments: IReferenceSelectionData[];
  advancedGarmentSearch: boolean;
}

const defaultState: SelectionModalState = {
  pagination: {
    currentPage: 1,
    pageCount: 1,
    pageSize: 10,
    recordCount: 1,
  },
  searchPattern: '',
  filters: {},
  visibleFilter: '',
  garmentsLoaded: true,
  garmentsLoading: false,
  allGarments: [],
  advancedGarmentSearch: false,
};

@Injectable()
export class SelectionModalStore extends ComponentStore<SelectionModalState> {
  constructor() {
    super(defaultState);
  }

  readonly currentPage$ = this.select(
    ({ pagination }) => pagination.currentPage,
  );

  readonly searchPattern$ = this.select(
    ({ searchPattern: searchPattern }) => searchPattern,
  );

  readonly filters$ = this.select(({ filters: filters }) => filters);
  readonly garmentsLoaded$ = this.select(
    ({ garmentsLoaded: garmentsLoaded }) => garmentsLoaded,
  );
  readonly garmentsLoading$ = this.select(
    ({ garmentsLoading: garmentsLoading }) => garmentsLoading,
  );
  readonly allGarments$ = this.select(
    ({ allGarments: allGarments }) => allGarments,
  );
  readonly advancedGarmentSearch$ = this.select(
    ({ advancedGarmentSearch: advancedGarmentSearch }) => advancedGarmentSearch,
  );

  readonly countFilters$ = this.select(({ filters: f }) => {
    return Object.keys(f).length;
  });

  readonly visibleFilter$ = this.select(
    ({ visibleFilter: visibleFilter }) => visibleFilter,
  );

  readonly updateFilters = this.updater((state, filter: FilterOutput) => {
    const f = JSON.parse(JSON.stringify(state.filters));
    if (
      filter.value === undefined ||
      filter.value === '' ||
      filter.value === '[]'
    ) {
      delete f[filter.id];
    } else {
      f[filter.id] = filter.value;
    }
    return {
      ...state,
      filters: f,
    };
  });

  readonly clearFilters = this.updater((state) => {
    return { ...state, filters: {} };
  });

  readonly clearFilter = this.updater((state, id: string) => {
    const f = JSON.parse(JSON.stringify(state.filters));
    delete f[id];
    return {
      ...state,
      filters: f,
    };
  });

  readonly updateCurrentPage = this.updater(
    (state, currentPage: number | null) => ({
      ...state,
      pagination: { ...state.pagination, currentPage: currentPage },
    }),
  );

  readonly updatePageSize = this.updater((state, pageSize: number | null) => ({
    ...state,
    pagination: { ...state.pagination, pageSize: pageSize },
  }));

  readonly updateGarmentsLoaded = this.updater(
    (state, garmentsLoaded: boolean) => ({
      ...state,
      garmentsLoaded: garmentsLoaded,
    }),
  );

  readonly updateGarmentsLoading = this.updater(
    (state, garmentsLoading: boolean) => ({
      ...state,
      garmentsLoading: garmentsLoading,
    }),
  );

  readonly updateAllGarments = this.updater(
    (state, allGarments: IReferenceSelectionData[]) => ({
      ...state,
      allGarments: allGarments,
    }),
  );

  readonly updateAdvancedGarmentSearch = this.updater(
    (state, advancedGarmentSearch: boolean) => ({
      ...state,
      advancedGarmentSearch: advancedGarmentSearch,
    }),
  );

  readonly updatePageCount = this.updater(
    (state, pageCount: number | null) => ({
      ...state,
      pagination: { ...state.pagination, pageCount: pageCount },
    }),
  );

  readonly updateSearchPattern = this.updater(
    (state, searchPattern: string) => ({
      ...state,
      pagination: { ...state.pagination, currentPage: 1 },
      searchPattern: searchPattern,
    }),
  );

  readonly updateVisibleFilter = this.updater(
    (state, visibleFilterId: string) => ({
      ...state,
      visibleFilter: visibleFilterId,
    }),
  );
}
